import React, { useState, useEffect, Fragment, useRef } from 'react'
import { Transition } from '@headlessui/react'
import { useTranslation } from 'react-i18next'
import { getPath, getLanguage } from '../../util/helper.js'
import { BsCheck } from 'react-icons/bs'
import { globalHistory } from '@reach/router'
import { useWindowSize } from '../../util/hooks.js'
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from 'body-scroll-lock'
import PropTypes from 'prop-types'
import cx from 'classnames'
import Link from '../Link'
import Logo from './Logo.js'
import Hamburger from './Hamburger'
import ChevronRight from '../../assets/icons/chevron-right.svg'
import ChevronLeft from '../../assets/icons/chevron-left.svg'
import Globe from '../../assets/icons/globe.svg'

const DEFAULT_LOCALE = process.env.GATSBY_DEFAULT_LOCALE
const COUNTRY = process.env.GATSBY_COUNTRY

const LanguageSwitcher = ({ data }) => {
  const { t, i18n } = useTranslation()
  const [show, setShow] = useState(false)

  return (
    <div className="relative flex flex-col items-center">
      <button
        className={cx(
          'inline-flex items-center px-2 py-3 border-b-4 border-transparent text-white',
          show ? 'border-blueLight-500' : 'border-transparent'
        )}
        onClick={() => setShow(!show)}
      >
        <Globe className="w-5 h-5 mr-2 text-white fill-current" />
        {t('Language')}
      </button>
      {show ? (
        <div className="w-full px-2 py-3 bg-blue-500 top-full">
          {Object.keys(data).map((lang) => {
            const active = lang === i18n.language
            return (
              <Link
                key={`${data[lang]}-${lang}`}
                className={cx(
                  'inline-flex items-center px-4 py-1',
                  active ? 'text-blueLight-500' : 'text-white'
                )}
                url={`${lang !== DEFAULT_LOCALE ? `/${lang}` : ''}/${
                  data[lang]
                }`}
                onClick={() => i18n.changeLanguage(lang)}
              >
                {getLanguage(lang)}
                {active && (
                  <BsCheck className="w-5 h-5 ml-2 fill-current text-blueLight-500" />
                )}
              </Link>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

const SiteHeaderMobile = ({ data, navConfig, isLoggedIn, styleOption }) => {
  const { t } = useTranslation()
  const { disableLogoLinks, isTransparent, hideNavbarItems, localeSlugs } =
    navConfig
  const listingsPath = getPath('listings')
  const menuElement = useRef(null)
  const [open, setOpen] = useState(false)
  const [drawer, setDrawer] = useState({ label: '', items: [] })
  const drawerOpen = drawer.items.length > 0
  const { height, width } = useWindowSize()

  const closeDrawer = () => {
    setDrawer({ label: '', items: [] })
  }

  // Close menu on wide screen
  useEffect(() => {
    if (width >= 1200) setOpen(false)
  }, [width])

  // Close menu on route changes
  useEffect(() => {
    return globalHistory.listen(({ action }) => {
      if (action === 'PUSH') setOpen(false)
    })
  }, [setOpen])

  // Disable/enable scrolling
  useEffect(() => {
    if (open) {
      disableBodyScroll(menuElement.current)
    } else {
      enableBodyScroll(menuElement?.current)
    }
    return () => clearAllBodyScrollLocks()
  }, [open, menuElement, menuElement?.current])

  // Close drawer when menu is closed
  useEffect(() => {
    if (!open) closeDrawer()
  }, [open])

  const logo = (
    <Logo
      navConfig={navConfig}
      blackLogo={data.logoBlack}
      lightLogo={data.logo}
      menuOpen={open}
      alt="Blok"
    />
  )

  let bgColor

  if (styleOption === 'Lead') bgColor = 'bg-charcoal-500'
  else bgColor = 'bg-blue-500'

  return (
    <div
      //Todo: color option here for top header.
      className={cx(
        'w-full',
        open ? 'z-50' : 'z-30',
        (!open && isTransparent) ? '' : bgColor,
        {
          relative: !open && !isTransparent,
          absolute: !open && isTransparent,
        }
      )}
    >
      <div
        className={cx(
          'container relative items-center py-4',
          hideNavbarItems ? 'hidden' : 'flex xl:hidden',
          {
            'z-10': open,
          }
        )}
      >
        <div
          className="flex-grow-0 flex-shrink-0"
          style={{ flexBasis: COUNTRY.includes('LEAD') ? '140px' : '70px' }}
        >
          {disableLogoLinks ? (
            <>{logo}</>
          ) : (
            <Link url={getPath('/')}>{logo}</Link>
          )}
        </div>
        <div className="ml-auto" onClick={() => setOpen((prev) => !prev)}>
          <Hamburger
            open={open}
            colorOption={
              COUNTRY.includes('LEAD') ? 'bg-lime-500' : 'bg-pink-500'
            }
          />
        </div>
      </div>
      <div
        ref={menuElement}
        className={cx(
          // Todo: color option here for mobile nav.
          'overflow-x-hidden overflow-y-auto transition-opacity duration-300',
          styleOption === 'Lead' ? 'bg-charcoal-500' : 'bg-blue-500',
          open ? 'opacity-100' : 'opacity-0'
        )}
        style={{ height: open ? `${height - 70}px` : 0 }}
      >
        <Transition
          show={open}
          appear={true}
          enter="transition duration-300"
          enterFrom="-translate-y-6"
          enterTo="translate-y-0"
        >
          <Transition
            show={!drawerOpen}
            enter="transition-opacity duration-150 delay-150"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            className="relative pb-8"
          >
            <div className="container relative z-0 pb-8">
              <div className="flex flex-col items-center text-center">
                {data.phoneNumber ? (
                  <>
                    <p className="mb-1 text-white">{data.phoneTitle}</p>
                    <a
                      className="text-4xl font-bold text-white"
                      href={`tel:${data.phoneNumber}`}
                    >
                      {data.phoneNumber}
                    </a>
                    <p className="text-sm text-white">{data.phoneHelpText}</p>
                    {data.phoneHelpText2 ? 
                      <p className="text-sm text-white font-bold mt-2">
                        {data.phoneHelpText2}
                      </p>
                    : null}
                  </>
                ) : null}
                {data.appPriceValuationButtonLabel &&
                data.appPriceValuationButtonLink ? (
                  <a
                    href={data.appPriceValuationButtonLink}
                    className={`block max-w-lg mx-auto my-6 btn ${
                      COUNTRY.includes('LEAD') ? 'btn-lime' : 'btn-pink'
                    }`}
                  >
                    {data.appPriceValuationButtonLabel}
                  </a>
                ) : null}
                {data.leftNavigation
                  .concat(data.rightNavigation)
                  .map(
                    ({
                      id,
                      itemLabel,
                      itemLink,
                      isDropdownType,
                      dropdownItems,
                    }) => {
                      if (isDropdownType) {
                        return (
                          <button
                            key={id}
                            className="inline-flex text-white items-center justify-center p-2 border-transparent border-b-[3px] hover:border-blueLight-500"
                            onClick={() =>
                              setDrawer({
                                label: itemLabel,
                                items: dropdownItems,
                              })
                            }
                          >
                            {itemLabel}
                            <ChevronRight className="w-4 h-4 ml-2 fill-current" />
                          </button>
                        )
                      }

                      return (
                        <Link
                          key={id}
                          url={itemLink}
                          className="inline-block px-2 py-3 text-white"
                        >
                          {itemLabel}
                        </Link>
                      )
                    }
                  )}

                {data?.displayLanguageSwitcher ? (
                  <LanguageSwitcher data={localeSlugs} />
                ) : null}
              </div>
              {data.appLoginButtonLabel &&
              data.appLoggedInButtonLabel &&
              !data.hideLoginButton ? (
                <a
                  href={
                    isLoggedIn
                      ? data.appLoggedInButtonLink
                      : data.appLoginButtonLink
                  }
                  className="block max-w-lg mx-auto mt-4 btn btn-white btn-sm"
                >
                  {isLoggedIn
                    ? data.appLoggedInButtonLabel
                    : data.appLoginButtonLabel}
                </a>
              ) : null}
              {data.displayStartSellingButton &&
              data.appStartSellingButtonLabel ? (
                <a
                  href={data.appStartSellingButtonLink}
                  className="block max-w-lg mx-auto mt-4 btn btn-white btn-sm"
                >
                  {data.appStartSellingButtonLabel}
                </a>
              ) : null}
            </div>
          </Transition>
          <Transition
            show={drawerOpen}
            enter="transition-all transform duration-150"
            enterFrom="translate-x-6 opacity-0"
            enterTo="translate-x-0 opacity-100"
            className="relative pb-8"
          >
            <div className="container sticky top-0 flex items-center justify-center h-10 bg-blue-500">
              <button
                aria-label={t('Back to main menu')}
                onClick={() => closeDrawer()}
                className="absolute p-2.5 left-2"
              >
                <ChevronLeft className="w-6 h-6 text-white fill-current" />
              </button>
              <div className="pt-1 text-sm font-bold tracking-widest text-center text-white uppercase">
                {drawer.label}
              </div>
            </div>
            {drawer.items.map(
              ({
                id,
                itemLink,
                itemLabel,
                apartmentFiltersCity,
                apartmentsFilterType,
                dropdownHeaderBefore,
                showDividerAfter,
              }) => {
                if (apartmentsFilterType && apartmentFiltersCity) {
                  itemLink = `${listingsPath}?city=${apartmentFiltersCity}${
                    apartmentsFilterType !== 'sale'
                      ? `&${apartmentsFilterType}=true`
                      : ''
                  }`
                } else if (apartmentsFilterType) {
                  itemLink = `${listingsPath}${
                    apartmentsFilterType !== 'sale'
                      ? `?${apartmentsFilterType}=true`
                      : ''
                  }`
                }

                return (
                  <Fragment key={id}>
                    {dropdownHeaderBefore ? (
                      <h6 className="px-6 py-3 text-center text-blue-300">
                        {dropdownHeaderBefore}
                      </h6>
                    ) : null}
                    <Link
                      className="text-center block px-4 py-1.5 text-white hover:text-blueLight-500"
                      url={itemLink}
                    >
                      {itemLabel}
                    </Link>
                    {showDividerAfter ? (
                      <div className="w-full h-px max-w-xs mx-auto my-2 bg-blue-400" />
                    ) : null}
                  </Fragment>
                )
              }
            )}
          </Transition>
        </Transition>
      </div>
    </div>
  )
}

SiteHeaderMobile.propTypes = {
  data: PropTypes.object.isRequired,
  navConfig: PropTypes.object.isRequired,
  isLoggedIn: PropTypes.bool,
}

LanguageSwitcher.propTypes = {
  data: PropTypes.object.isRequired,
}

export default SiteHeaderMobile
